import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getPropertyDetails, type PropertyUnit } from "@/repo/property";
import { heatColor } from "@/constants";
import TabList, { type TabData } from "../TabList";
import "./PropertyDetail.css";

type UnitTab = "attributes" | "epcs" | "sales";

interface PropertyDetailProps {
    propertyId: number;
    onClose(): void;
}

function formatPrice(p: number | null | undefined): string {
    if (p == null) return "—";
    if (p >= 1_000_000) return `£${(p / 1_000_000).toFixed(2)}M`;
    if (p >= 1_000) return `£${Math.round(p / 1000)}k`;
    return `£${p}`;
}

function formatDate(d: string | null): string {
    if (!d) return "—";
    return d.slice(0, 10);
}

function formatAttributeName(name: string): string {
    return name
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
}

const RATING_COLOURS: Record<string, string> = {
    A: "#008054",
    B: "#19b459",
    C: "#8dce46",
    D: "#ffd500",
    E: "#fcaa1a",
    F: "#ef8023",
    G: "#e9153b",
};

function DebugJson({ label, value }: { label: string; value: unknown }) {
    if (value == null) return null;
    return (
        <details className="property-detail__debug">
            <summary>{label}</summary>
            <pre>{JSON.stringify(value, null, 2)}</pre>
        </details>
    );
}

function RatingBadge({ rating }: { rating: string | null | undefined }) {
    if (!rating) {
        return (
            <span className="property-rating-badge property-rating-badge--unknown">
                ?
            </span>
        );
    }
    const r = rating.trim().toUpperCase();
    const colour = RATING_COLOURS[r] ?? "#888";
    return (
        <span className="property-rating-badge" style={{ background: colour }}>
            {r}
        </span>
    );
}

function UnitRow({ unit, onSelect }: { unit: PropertyUnit; onSelect(): void }) {
    return (
        <button type="button" className="property-unit-row" onClick={onSelect}>
            <RatingBadge rating={unit.latest_epc_rating} />
            <div className="property-unit-row__main">
                <div className="property-unit-row__address">
                    {unit.address ?? `UPRN ${unit.uprn}`}
                </div>
                <div className="property-unit-row__meta">
                    {unit.postcode ?? "—"}
                    {" · "}
                    {unit.sales.length} sale{unit.sales.length === 1 ? "" : "s"}
                    {" · "}
                    {unit.epcs.length} EPC{unit.epcs.length === 1 ? "" : "s"}
                </div>
            </div>
            <div className="property-unit-row__price">
                <div>{formatPrice(unit.latest_sale_price)}</div>
                <div className="property-unit-row__date">
                    {unit.latest_sale_date
                        ? formatDate(unit.latest_sale_date)
                        : ""}
                </div>
            </div>
        </button>
    );
}

function UnitDetail({ unit, onBack }: { unit: PropertyUnit; onBack(): void }) {
    const attributeEntries = Object.entries(unit.attributes);
    const [unitTab, setUnitTab] = useState<UnitTab>("attributes");

    const unitTabs: TabData<UnitTab>[] = [
        { id: "attributes", label: "Attributes" },
        { id: "epcs", label: "EPCs", count: unit.epcs.length },
        { id: "sales", label: "Sales", count: unit.sales.length },
    ];

    return (
        <div className="property-detail">
            <div className="property-detail__header">
                <button
                    type="button"
                    className="property-detail__close"
                    onClick={onBack}
                >
                    ←
                </button>
                <div>
                    <div className="property-detail__title">
                        {unit.address ?? `UPRN ${unit.uprn}`}
                    </div>
                    <div className="property-detail__subtitle">
                        {unit.postcode ?? "—"} · UPRN {unit.uprn}
                    </div>
                </div>
                <a
                    className="property-detail__zoopla"
                    href={`https://www.zoopla.co.uk/property/uprn/${unit.uprn}`}
                    target="_blank"
                    rel="noreferrer"
                >
                    Open on Zoopla ↗
                </a>
            </div>

            <div className="property-detail__tabs">
                <div className="property-detail__tabbar">
                    <TabList
                        tabs={unitTabs}
                        current={unitTab}
                        setCurrent={setUnitTab}
                        size="small"
                    />
                </div>

                {unitTab === "attributes" && attributeEntries.length > 0 && (
                    <section>
                        <div className="property-detail__attributes">
                            {attributeEntries.map(([name, score]) => (
                                <div key={name}>
                                    <div className="property-detail__attribute-label">
                                        <span>{formatAttributeName(name)}</span>
                                        <span>{Math.round(score * 100)}%</span>
                                    </div>
                                    <div className="property-detail__attribute-track">
                                        <div
                                            className="property-detail__attribute-fill"
                                            style={{
                                                width: `${Math.round(score * 100)}%`,
                                                background: heatColor(score),
                                            }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}
                {unitTab === "attributes" && attributeEntries.length === 0 && (
                    <div className="property-detail__note">No attributes on file.</div>
                )}

                {unitTab === "epcs" && (
                    <section>
                        {unit.epcs.length === 0 ? (
                            <div className="property-detail__note">No EPC on file.</div>
                        ) : (
                            <div className="property-detail__cards">
                                {unit.epcs.map((e) => (
                                    <div
                                        className="property-detail__card"
                                        key={e.lmk_key}
                                    >
                                        <div className="property-detail__card-row">
                                            <RatingBadge
                                                rating={e.current_energy_rating}
                                            />
                                            <div>
                                                <div className="property-detail__card-title">
                                                    {e.address1 ?? "—"}
                                                </div>
                                                <div className="property-detail__card-sub">
                                                    {e.postcode}
                                                    {e.lodgement_date &&
                                                        ` · ${formatDate(e.lodgement_date)}`}
                                                </div>
                                            </div>
                                        </div>
                                        <dl className="property-detail__dl">
                                            {e.current_energy_efficiency != null && (
                                                <>
                                                    <dt>Efficiency</dt>
                                                    <dd>
                                                        {e.current_energy_efficiency}
                                                        {e.potential_energy_efficiency !=
                                                            null &&
                                                            ` → ${e.potential_energy_efficiency}`}
                                                    </dd>
                                                </>
                                            )}
                                            {e.property_type && (
                                                <>
                                                    <dt>Type</dt>
                                                    <dd>
                                                        {e.property_type}
                                                        {e.built_form &&
                                                            ` · ${e.built_form}`}
                                                    </dd>
                                                </>
                                            )}
                                            {e.construction_age_band && (
                                                <>
                                                    <dt>Age</dt>
                                                    <dd>{e.construction_age_band}</dd>
                                                </>
                                            )}
                                            {e.total_floor_area != null && (
                                                <>
                                                    <dt>Floor area</dt>
                                                    <dd>{e.total_floor_area} m²</dd>
                                                </>
                                            )}
                                            {e.main_fuel && (
                                                <>
                                                    <dt>Fuel</dt>
                                                    <dd>{e.main_fuel}</dd>
                                                </>
                                            )}
                                            {e.tenure && (
                                                <>
                                                    <dt>Tenure</dt>
                                                    <dd>{e.tenure}</dd>
                                                </>
                                            )}
                                        </dl>
                                        <DebugJson label="raw EPC row" value={e.raw} />
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                )}

                {unitTab === "sales" && (
                    <section>
                        {unit.sales.length === 0 ? (
                            <div className="property-detail__note">
                                No sales on file.
                            </div>
                        ) : (
                            <table className="property-detail__sales">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Price</th>
                                        <th>Type</th>
                                        <th>Duration</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {unit.sales.map((s) => (
                                        <tr key={s.uid}>
                                            <td>{formatDate(s.date_of_transfer)}</td>
                                            <td>{formatPrice(s.price)}</td>
                                            <td>{s.property_type ?? "—"}</td>
                                            <td>{s.duration ?? "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </section>
                )}
            </div>
        </div>
    );
}

function PropertyDetail(props: PropertyDetailProps) {
    const [selectedUprn, setSelectedUprn] = useState<number>();

    const query = useQuery({
        queryKey: ["property-details", props.propertyId],
        queryFn: () => getPropertyDetails(props.propertyId),
    });

    // Single-unit properties have no useful list to show — go straight to
    // the unit's detail view. "Back" closes the whole panel (there's no
    // list underneath it to go back to).
    if (query.data?.units.length === 1) {
        return <UnitDetail unit={query.data.units[0]} onBack={props.onClose} />;
    }

    if (selectedUprn != null && query.data) {
        const unit = query.data.units.find((u) => u.uprn === selectedUprn);
        if (unit) {
            return (
                <UnitDetail
                    unit={unit}
                    onBack={() => setSelectedUprn(undefined)}
                />
            );
        }
    }

    return (
        <div className="property-detail">
            <div className="property-detail__header">
                <button
                    type="button"
                    className="property-detail__close"
                    onClick={props.onClose}
                >
                    ←
                </button>
                <div>
                    <div className="property-detail__title">
                        Property #{props.propertyId}
                    </div>
                    {query.data && (
                        <div className="property-detail__subtitle">
                            {query.data.source.toUpperCase()} ·{" "}
                            {query.data.units.length} unit
                            {query.data.units.length === 1 ? "" : "s"}
                        </div>
                    )}
                </div>
            </div>

            {query.isPending && (
                <div className="property-detail__note">Loading…</div>
            )}
            {query.error && (
                <div className="property-detail__error">
                    {(query.error as Error).message}
                </div>
            )}

            {query.data && (
                <section>
                    {query.data.units.length === 0 ? (
                        <div className="property-detail__note">
                            No UPRNs linked to this polygon.
                        </div>
                    ) : (
                        <div className="property-unit-list">
                            {query.data.units.map((u) => (
                                <UnitRow
                                    key={u.uprn}
                                    unit={u}
                                    onSelect={() => setSelectedUprn(u.uprn)}
                                />
                            ))}
                        </div>
                    )}
                </section>
            )}
        </div>
    );
}

export default PropertyDetail;
